import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../utils/axiosConfig';
import { toast } from 'react-toastify';
import { X, Check, Home } from 'lucide-react';
import { createResizedPair } from '../utils/imageResize';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5159';

interface DaireOzellik {
    daireOzellikId?: number;
    daireTakipNo: string;
    ozellik: string;
    deger: string;
    aktif: boolean;
}

interface SunumBelge {
    sunumBelgeId: number;
    takipNo: string;
    aciklama?: string;
    sunumBelgeTuru: string;
    sunumBelgeAdi?: string;
    aktif: boolean;
    eklenmeTarihi: string;
}

interface Proje {
    projeID: number;
    projeAdi: string;
}

interface Gayrimenkul {
    gayrimenkulID: number;
    takipNo: string;
    kategori: number;
    baslik: string;
    adres: string;
    metrekare: number;
    kat: string | null;
    durum: number;
    projeID: number | null;
    kiraBedeli: number;
    aktifMi: boolean;
    proje: Proje | null;
}

interface GayrimenkulForm {
    kategori: number;
    baslik: string;
    adres: string;
    metrekare: number;
    kat: string;
    durum: number;
    projeID: string;
    kiraBedeli: number;
    aktifMi: boolean;
}

const KATEGORI_LABELS: Record<number, string> = { 0: 'Daire', 1: 'Ofis', 2: 'Dükkân' };
const DURUM_LABELS: Record<number, string> = { 0: 'Boş', 1: 'Kirada', 2: 'Bakımda' };

const DURUM_STR_TO_NUM: Record<string, number> = { 'Bos': 0, 'Kirada': 1, 'Bakimda': 2 };
const KATEGORI_STR_TO_NUM: Record<string, number> = { 'Daire': 0, 'Ofis': 1, 'Dukkan': 2 };

const normalizeGayrimenkul = (g: any): Gayrimenkul => ({
    ...g,
    durum: typeof g.durum === 'string' ? (DURUM_STR_TO_NUM[g.durum] ?? g.durum) : g.durum,
    kategori: typeof g.kategori === 'string' ? (KATEGORI_STR_TO_NUM[g.kategori] ?? g.kategori) : g.kategori,
});
const DURUM_COLORS: Record<number, { bg: string; color: string; dot: string }> = {
    0: { bg: '#dcfce7', color: '#15803d', dot: '#22c55e' },
    1: { bg: '#dbeafe', color: '#1d4ed8', dot: '#3b82f6' },
    2: { bg: '#fef3c7', color: '#b45309', dot: '#f59e0b' },
};

const emptyForm: GayrimenkulForm = {
    kategori: 0, baslik: '', adres: '', metrekare: 0,
    kat: '', durum: 0, projeID: '', kiraBedeli: 0, aktifMi: true,
};

const KiralikPortfoy: React.FC = () => {
    const navigate = useNavigate();
    const [gayrimenkuller, setGayrimenkuller] = useState<Gayrimenkul[]>([]);
    const [projeler, setProjeler] = useState<Proje[]>([]);
    const [selected, setSelected] = useState<Gayrimenkul | null>(null);
    const [showModal, setShowModal] = useState(false);
    const [hoveredRow, setHoveredRow] = useState<number | null>(null);
    const [saving, setSaving] = useState(false);
    const [formData, setFormData] = useState<GayrimenkulForm>({ ...emptyForm });
    const [searchTerm, setSearchTerm] = useState('');
    const [filterDurum, setFilterDurum] = useState<number[]>([0, 2]);
    const [filterKategori, setFilterKategori] = useState<number[]>([]);
    const [thumbErrors, setThumbErrors] = useState<Set<string>>(new Set());

    // Daire Özellikleri
    const [daireOzellikler, setDaireOzellikler] = useState<DaireOzellik[]>([]);
    const [ozellikOptions, setOzellikOptions] = useState<string[]>([]);
    const [degerOptionsMap, setDegerOptionsMap] = useState<Record<string, string[]>>({});

    // Sunum Belgeleri
    const [sunumBelgeler, setSunumBelgeler] = useState<SunumBelge[]>([]);
    const [sunumBelgeSaving, setSunumBelgeSaving] = useState(false);
    const [sunumForm, setSunumForm] = useState({ aciklama: '', tur: 'Görsel', videoLink: '' });
    const [sunumFile, setSunumFile] = useState<File | null>(null);
    const [showSunumEkleForm, setShowSunumEkleForm] = useState(false);

    // Thumbnail önizleme (liste)
    const [thumbnails, setThumbnails] = useState<Record<string, number | null>>({});

    // Sunum / Önizleme Modal
    const [showPreview, setShowPreview] = useState(false);
    const [previewGayrimenkul, setPreviewGayrimenkul] = useState<Gayrimenkul | null>(null);
    const [previewOzellikler, setPreviewOzellikler] = useState<DaireOzellik[]>([]);
    const [previewBelgeler, setPreviewBelgeler] = useState<SunumBelge[]>([]);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [carouselIndex, setCarouselIndex] = useState(0);
    const [lightboxBelge, setLightboxBelge] = useState<SunumBelge | null>(null);

    const handleOpenPreview = async (g: Gayrimenkul) => {
        setPreviewGayrimenkul(g);
        setShowPreview(true);
        setCarouselIndex(0);
        setLightboxBelge(null);
        setPreviewLoading(true);
        try {
            const [ozRes, belRes] = await Promise.all([
                axios.get(`/api/DaireOzellikler/daire/${g.takipNo}`),
                axios.get(`/api/SunumBelgeleri/takip/${g.takipNo}`),
            ]);
            setPreviewOzellikler(ozRes.data);
            setPreviewBelgeler(belRes.data);
        } catch {
            setPreviewOzellikler([]);
            setPreviewBelgeler([]);
        } finally {
            setPreviewLoading(false);
        }
    };

    const handleClosePreview = () => {
        setShowPreview(false);
        setPreviewGayrimenkul(null);
        setPreviewOzellikler([]);
        setPreviewBelgeler([]);
        setLightboxBelge(null);
    };

    const fetchOzellikOptions = async () => {
        try {
            const res = await axios.get('/api/DaireOzellikler/ozellikler');
            setOzellikOptions(res.data);
        } catch {}
    };

    const fetchDegerOptions = async (ozellik: string) => {
        if (!ozellik || degerOptionsMap[ozellik]) return;
        try {
            const res = await axios.get(`/api/DaireOzellikler/degerler?ozellik=${encodeURIComponent(ozellik)}`);
            setDegerOptionsMap(prev => ({ ...prev, [ozellik]: res.data }));
        } catch {}
    };

    const fetchDaireOzellikler = async (takipNo: string) => {
        try {
            const res = await axios.get(`/api/DaireOzellikler/daire/${takipNo}`);
            setDaireOzellikler(res.data);
        } catch {
            setDaireOzellikler([]);
        }
    };

    const saveDaireOzellikler = async (takipNo: string) => {
        for (const oz of daireOzellikler) {
            if (!oz.ozellik.trim() || !oz.deger.trim()) continue;
            if (oz.daireOzellikId) {
                await axios.post(`/api/DaireOzellikler/update/${oz.daireOzellikId}`, { ...oz, daireTakipNo: takipNo });
            } else {
                await axios.post('/api/DaireOzellikler', { daireTakipNo: takipNo, ozellik: oz.ozellik, deger: oz.deger, aktif: true });
            }
        }
    };

    const fetchSunumBelgeler = async (takipNo: string) => {
        try {
            const res = await axios.get(`/api/SunumBelgeleri/takip/${takipNo}`);
            setSunumBelgeler(res.data);
        } catch {
            setSunumBelgeler([]);
        }
    };

    const handleSunumYukle = async () => {
        if (!selected?.takipNo) return;
        setSunumBelgeSaving(true);
        try {
            const fd = new FormData();
            fd.append('takipNo', selected.takipNo);
            fd.append('aciklama', sunumForm.aciklama);
            fd.append('sunumBelgeTuru', sunumForm.tur);
            if (sunumForm.tur === 'Video') {
                fd.append('videoLink', sunumForm.videoLink);
            } else {
                if (!sunumFile) { toast.error('Dosya seçiniz'); setSunumBelgeSaving(false); return; }
                if (sunumForm.tur === 'Görsel') {
                    const { mainFile, thumbFile } = await createResizedPair(sunumFile);
                    fd.append('file', mainFile);
                    fd.append('thumbnailFile', thumbFile);
                } else {
                    fd.append('file', sunumFile);
                }
            }
            await axios.post('/api/SunumBelgeleri/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            toast.success('Sunum belgesi yüklendi');
            setSunumForm({ aciklama: '', tur: 'Görsel', videoLink: '' });
            setSunumFile(null);
            setShowSunumEkleForm(false);
            fetchSunumBelgeler(selected.takipNo);
        } catch {
            toast.error('Sunum belgesi yüklenirken hata oluştu');
        } finally {
            setSunumBelgeSaving(false);
        }
    };

    const handleSunumSil = async (id: number) => {
        try {
            await axios.delete(`/api/SunumBelgeleri/${id}`);
            toast.success('Sunum belgesi silindi');
            if (selected?.takipNo) fetchSunumBelgeler(selected.takipNo);
        } catch {
            toast.error('Silme işlemi başarısız');
        }
    };

    const fetchData = async () => {
        try {
            const [gRes, pRes] = await Promise.all([
                axios.get('/api/KiralikGayrimenkuller'),
                axios.get('/api/Projeler'),
            ]);
            const normalized: Gayrimenkul[] = gRes.data.map(normalizeGayrimenkul);
            setGayrimenkuller(normalized);
            setProjeler(pRes.data);
            // Thumbnail çek
            const thumbResults: Record<string, number | null> = {};
            await Promise.all(
                normalized.map(async (g: Gayrimenkul) => {
                    try {
                        const bRes = await axios.get(`/api/SunumBelgeleri/takip/${g.takipNo}`);
                        const gorseller = (bRes.data as SunumBelge[]).filter(
                            (b: SunumBelge) => b.sunumBelgeTuru === 'Görsel' && b.aktif
                        );
                        thumbResults[g.takipNo] = gorseller.length > 0 ? gorseller[0].sunumBelgeId : null;
                    } catch {
                        thumbResults[g.takipNo] = null;
                    }
                })
            );
            setThumbnails(thumbResults);
        } catch {
            toast.error('Veriler yüklenirken bir hata oluştu');
        }
    };

    useEffect(() => { fetchData(); }, []);

    useEffect(() => {
        if (showModal && selected?.takipNo) {
            fetchDaireOzellikler(selected.takipNo);
            fetchOzellikOptions();
            fetchSunumBelgeler(selected.takipNo);
        } else if (!showModal) {
            setDaireOzellikler([]);
            setSunumBelgeler([]);
            setShowSunumEkleForm(false);
        }
    }, [showModal, selected?.takipNo]); // eslint-disable-line react-hooks/exhaustive-deps

    const filtered = gayrimenkuller.filter(g => {
        const matchSearch = !searchTerm ||
            g.baslik.toLowerCase().includes(searchTerm.toLowerCase()) ||
            g.takipNo.toLowerCase().includes(searchTerm.toLowerCase()) ||
            g.adres.toLowerCase().includes(searchTerm.toLowerCase());
        const matchDurum = filterDurum.length === 0 || filterDurum.includes(Number(g.durum));
        const matchKategori = filterKategori.length === 0 || filterKategori.includes(Number(g.kategori));
        return matchSearch && matchDurum && matchKategori;
    });

    const currentEditIndex = selected ? filtered.findIndex(g => g.gayrimenkulID === selected.gayrimenkulID) : -1;
    const currentPreviewIndex = previewGayrimenkul ? filtered.findIndex(g => g.gayrimenkulID === previewGayrimenkul.gayrimenkulID) : -1;

    const goEditPrev = () => {
        if (currentEditIndex <= 0) return;
        handleEdit(filtered[currentEditIndex - 1]);
    };
    const goEditNext = () => {
        if (currentEditIndex < 0 || currentEditIndex >= filtered.length - 1) return;
        handleEdit(filtered[currentEditIndex + 1]);
    };
    const goPreviewPrev = () => {
        if (currentPreviewIndex <= 0) return;
        handleOpenPreview(filtered[currentPreviewIndex - 1]);
    };
    const goPreviewNext = () => {
        if (currentPreviewIndex < 0 || currentPreviewIndex >= filtered.length - 1) return;
        handleOpenPreview(filtered[currentPreviewIndex + 1]);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);
        const payload = {
            ...formData,
            projeID: formData.projeID ? parseInt(formData.projeID) : null,
        };
        try {
            if (selected) {
                try {
                    await axios.put(`/api/KiralikGayrimenkuller/${selected.gayrimenkulID}`, payload);
                } catch {
                    await axios.post(`/api/KiralikGayrimenkuller/update/${selected.gayrimenkulID}`, payload);
                }
                if (selected.takipNo) {
                    await saveDaireOzellikler(selected.takipNo);
                }
                toast.success('Gayrimenkul güncellendi');
            } else {
                await axios.post('/api/KiralikGayrimenkuller', payload);
                toast.success('Gayrimenkul eklendi');
            }
            fetchData();
            handleCloseModal();
        } catch {
            toast.error('İşlem sırasında bir hata oluştu');
        } finally {
            setSaving(false);
        }
    };

    const handleEdit = (g: Gayrimenkul) => {
        setSelected(g);
        setFormData({
            kategori: g.kategori,
            baslik: g.baslik,
            adres: g.adres,
            metrekare: g.metrekare,
            kat: g.kat || '',
            durum: g.durum,
            projeID: g.projeID?.toString() || '',
            kiraBedeli: g.kiraBedeli ?? 0,
            aktifMi: g.aktifMi,
        });
        setShowModal(true);
    };

    const handleDelete = async (g: Gayrimenkul) => {
        if (!window.confirm(`"${g.baslik}" gayrimenkulünü pasife almak istediğinize emin misiniz?`)) return;
        try {
            await axios.delete(`/api/KiralikGayrimenkuller/${g.gayrimenkulID}`);
            toast.success('Gayrimenkul pasife alındı');
            fetchData();
        } catch {
            toast.error('Silme işlemi sırasında hata oluştu');
        }
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelected(null);
        setFormData({ ...emptyForm });
        setDaireOzellikler([]);
        setSunumBelgeler([]);
        setShowSunumEkleForm(false);
        setSunumFile(null);
        setSunumForm({ aciklama: '', tur: 'Görsel', videoLink: '' });
    };

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

                .kp-page {
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
                    padding: 24px;
                    background: #f4f6fb;
                    min-height: 100vh;
                }
                .kp-header {
                    display: flex; align-items: flex-start; justify-content: space-between;
                    margin-bottom: 40px;
                }
                .kp-header-left { display: flex; flex-direction: column; gap: 4px; }
                .kp-title { font-size: 22px; font-weight: 700; color: #0f172a; letter-spacing: -0.4px; margin: 0; }
                .kp-subtitle { font-size: 13.5px; color: #64748b; font-weight: 400; margin: 0; }
                .kp-add-btn {
                    display: inline-flex; align-items: center; gap: 8px;
                    padding: 10px 20px; background: #cd6906; color: #fff;
                    border: none; border-radius: 8px; font-size: 14px; font-weight: 600;
                    font-family: inherit; cursor: pointer;
                    box-shadow: 0 4px 12px rgba(205,105,6,0.3);
                    transition: background 0.18s, transform 0.12s;
                }
                .kp-add-btn:hover { background: #b85d05; transform: translateY(-1px); }
                .kp-add-btn i { font-size: 14px; }

                /* Filter bar */
                .kp-filters {
                    display: flex; gap: 10px; margin-bottom: 16px; flex-wrap: wrap;
                    align-items: center;
                    background: #fff; border-radius: 12px; border: 1.5px solid #f1f5f9;
                    padding: 10px 16px; box-shadow: 0 1px 3px rgba(15,23,42,0.04);
                }
                .kp-search {
                    padding: 8px 14px 8px 36px; border: 1.5px solid #e2e8f0;
                    border-radius: 9px; font-size: 13px; font-family: inherit;
                    color: #0f172a; outline: none; min-width: 220px; flex: 1;
                    background: #f8fafc url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2.5' stroke-linecap='round'%3E%3Ccircle cx='11' cy='11' r='8'/%3E%3Cline x1='21' y1='21' x2='16.65' y2='16.65'/%3E%3C/svg%3E") no-repeat 12px center;
                    transition: border-color 0.15s, box-shadow 0.15s;
                }
                .kp-search:focus { border-color: #cd6906; box-shadow: 0 0 0 3px rgba(205,105,6,0.12); background: #fff; }
                .kp-filter-sep {
                    width: 1px; height: 26px; background: #e2e8f0; flex-shrink: 0;
                }
                .kp-filter-select {
                    padding: 9px 32px 9px 14px; border: 1.5px solid #e2e8f0;
                    border-radius: 9px; font-size: 13px; font-weight: 600;
                    font-family: inherit; color: #0f172a; background: #fff;
                    cursor: pointer; outline: none; appearance: none;
                    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2.5' stroke-linecap='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
                    background-repeat: no-repeat; background-position: right 10px center;
                }
                .kp-filter-group-label {
                    font-size: 11px; font-weight: 700; color: #94a3b8;
                    text-transform: uppercase; letter-spacing: 0.05em; white-space: nowrap;
                }
                .kp-checkbox-group {
                    display: flex; align-items: center; gap: 5px; flex-wrap: wrap;
                }
                .kp-checkbox-label {
                    font-size: 11px; font-weight: 700; color: #94a3b8; margin-right: 2px;
                    white-space: nowrap; text-transform: uppercase; letter-spacing: 0.05em;
                }
                .kp-checkbox-item {
                    display: flex; align-items: center; gap: 5px;
                    padding: 5px 11px; border: 1.5px solid #e2e8f0; border-radius: 8px;
                    font-size: 12.5px; font-weight: 600; color: #475569; cursor: pointer;
                    background: #fff; transition: all 0.15s; user-select: none;
                }
                .kp-checkbox-item:hover { border-color: #cbd5e1; background: #f8fafc; }
                .kp-checkbox-item.active { border-color: #cd6906; background: #fff7ed; color: #cd6906; }
                .kp-checkbox-item input[type="checkbox"] { display: none; }

                /* Card & Table */
                .kp-card {
                    background: #fff; border-radius: 14px;
                    box-shadow: 0 1px 3px rgba(15,23,42,0.05);
                    border: 1px solid #e9edf2;
                    overflow: hidden;
                }
                .kp-table { width: 100%; border-collapse: collapse; table-layout: fixed; }
                .kp-table thead tr { border-bottom: 1.5px solid #f1f5f9; }
                .kp-table thead th {
                    padding: 12px 16px; font-size: 10.5px; font-weight: 700;
                    letter-spacing: 0.06em; color: #94a3b8; text-align: left;
                    white-space: nowrap; text-transform: uppercase;
                }
                .kp-table thead th.kp-th-right { text-align: right; }
                .kp-table thead th.kp-th-actions { text-align: right; }
                .kp-table tbody tr {
                    border-bottom: 1px solid #f8fafc; transition: background 0.13s;
                }
                .kp-table tbody tr:last-child { border-bottom: none; }
                .kp-table tbody tr.kp-row-hovered { background: #f8faff; }
                .kp-table tbody td {
                    padding: 10px 12px; font-size: 13px; color: #1e293b; vertical-align: middle;
                    overflow: hidden;
                }

                .kp-cell-takip {
                    display: inline-block; font-size: 11px; color: #64748b; font-weight: 700;
                    font-family: 'JetBrains Mono', 'Fira Code', monospace;
                    background: #f1f5f9; padding: 3px 8px; border-radius: 6px;
                }
                .kp-cell-baslik { font-weight: 600; color: #0f172a; font-size: 14px; }
                .kp-cell-adres { font-size: 12px; color: #64748b; margin-top: 2px; }
                .kp-cell-meta { font-size: 13px; color: #475569; font-weight: 500; text-align: right; font-variant-numeric: tabular-nums; }
                .kp-cell-proje { font-size: 12px; color: #475569; font-weight: 600; }

                /* Soft badges */
                .kp-pill {
                    display: inline-flex; align-items: center; gap: 5px;
                    padding: 2px 10px 2px 8px; border-radius: 999px;
                    font-size: 11.5px; font-weight: 600; white-space: nowrap;
                }
                .kp-pill-dot { width: 6px; height: 6px; border-radius: 50%; flex-shrink: 0; }
                .kp-kategori-pill {
                    background: #f1f5f9; color: #475569; font-size: 11px; font-weight: 700;
                    padding: 2px 8px; border-radius: 6px;
                }

                /* Ghost buttons */
                .kp-actions {
                    display: flex; justify-content: flex-end; gap: 4px;
                }
                .kp-ghost-btn {
                    display: inline-flex; align-items: center; justify-content: center;
                    width: 30px; height: 30px; border-radius: 7px;
                    border: 1.5px solid #e2e8f0; background: #f8fafc;
                    cursor: pointer; transition: background 0.15s, border-color 0.15s; color: #64748b;
                    flex-shrink: 0;
                }
                .kp-ghost-edit:hover { background: #fff8f0; border-color: #f5d5a0; color: #cd6906; }
                .kp-ghost-delete:hover { background: #fff1f2; border-color: #fecdd3; color: #e11d48; }
                .kp-ghost-view:hover { background: #f0f9ff; border-color: #bae6fd; color: #0284c7; }
                .kp-ghost-btn i { font-size: 12px; }

                /* Footer */
                .kp-footer {
                    padding: 13px 18px; border-top: 1.5px solid #f1f5f9;
                    display: flex; align-items: center; justify-content: space-between;
                }
                .kp-count { font-size: 13px; color: #94a3b8; font-weight: 500; }
                .kp-count span { color: #475569; font-weight: 600; }

                /* Empty */
                /* Thumbnail */
                .kp-thumb {
                    width: 48px; height: 48px; border-radius: 10px; overflow: hidden;
                    background: #f1f5f9; border: 1px solid #e2e8f0; flex-shrink: 0;
                    display: flex; align-items: center; justify-content: center;
                }
                .kp-thumb img { width: 100%; height: 100%; object-fit: cover; display: block; }
                .kp-thumb-empty { color: #cbd5e1; display: flex; align-items: center; justify-content: center; }

                .kp-empty { text-align: center; padding: 56px 20px; color: #94a3b8; }
                .kp-empty-icon { display: flex; justify-content: center; margin-bottom: 10px; opacity: 0.6; }
                .kp-empty-text { font-size: 15px; font-weight: 500; }

                /* Stats */
                .kp-stats {
                    display: flex; gap: 10px; margin-bottom: 16px; flex-wrap: wrap;
                }
                .kp-stat {
                    display: flex; align-items: center; gap: 8px;
                    background: #fff; border-radius: 10px; border: 1.5px solid #f1f5f9;
                    padding: 8px 14px; box-shadow: 0 1px 3px rgba(15,23,42,0.04);
                }
                .kp-stat-num { font-size: 15px; font-weight: 800; color: #0f172a; }
                .kp-stat-label { font-size: 11.5px; font-weight: 600; color: #64748b; }

                /* ── Modal ── */
                .kp-overlay {
                    position: fixed; inset: 0;
                    background: rgba(15,23,42,0.45); backdrop-filter: blur(3px);
                    display: flex; align-items: center; justify-content: center;
                    z-index: 1050; padding: 20px;
                }
                .kp-modal {
                    background: #fff; border-radius: 16px;
                    width: 100%; max-width: 680px;
                    box-shadow: 0 20px 60px rgba(15,23,42,0.25);
                    animation: kpModalIn 0.18s ease; overflow: hidden;
                    max-height: 90vh; display: flex; flex-direction: column;
                }
                .kp-modal form { display: flex; flex-direction: column; flex: 1; min-height: 0; overflow: hidden; }
                @keyframes kpModalIn {
                    from { opacity: 0; transform: translateY(-12px) scale(0.97); }
                    to   { opacity: 1; transform: translateY(0) scale(1); }
                }
                .kp-modal-header {
                    padding: 22px 24px 18px; border-bottom: 1px solid #f1f5f9;
                    display: flex; align-items: center; justify-content: space-between;
                }
                .kp-modal-title { font-size: 16px; font-weight: 700; color: #0f172a; margin: 0; }
                .kp-modal-close {
                    width: 30px; height: 30px; border-radius: 8px; border: none;
                    background: #f1f5f9; color: #64748b; cursor: pointer;
                    display: flex; align-items: center; justify-content: center;
                    font-size: 16px; transition: background 0.15s;
                }
                .kp-modal-close:hover { background: #e2e8f0; color: #0f172a; }
                .kp-modal-body {
                    padding: 22px 24px; display: flex; flex-direction: column; gap: 14px;
                    overflow-y: auto; flex: 1; min-height: 0;
                }
                .kp-form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
                .kp-field label {
                    display: block; font-size: 12px; font-weight: 600;
                    text-transform: uppercase; letter-spacing: 0.07em;
                    color: #64748b; margin-bottom: 6px;
                }
                .kp-field input, .kp-field select {
                    width: 100%; padding: 10px 14px;
                    border: 1.5px solid #e2e8f0; border-radius: 9px;
                    font-size: 14px; font-family: inherit; color: #0f172a;
                    outline: none; box-sizing: border-box;
                    transition: border-color 0.15s, box-shadow 0.15s;
                }
                .kp-field input:focus, .kp-field select:focus {
                    border-color: #cd6906; box-shadow: 0 0 0 3px rgba(205,105,6,0.12);
                }
                .kp-toggle-row {
                    display: flex; align-items: center; justify-content: space-between;
                    padding: 12px 14px; background: #f8fafc; border-radius: 10px;
                    border: 1.5px solid #e2e8f0;
                }
                .kp-toggle-label { font-size: 14px; font-weight: 500; color: #334155; }
                .kp-switch { position: relative; width: 42px; height: 24px; flex-shrink: 0; }
                .kp-switch input { opacity: 0; width: 0; height: 0; }
                .kp-switch-track {
                    position: absolute; inset: 0; border-radius: 999px;
                    background: #cbd5e1; cursor: pointer; transition: background 0.2s;
                }
                .kp-switch input:checked + .kp-switch-track { background: #22c55e; }
                .kp-switch-track::after {
                    content: ''; position: absolute;
                    top: 3px; left: 3px; width: 18px; height: 18px;
                    border-radius: 50%; background: #fff;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.18); transition: transform 0.2s;
                }
                .kp-switch input:checked + .kp-switch-track::after { transform: translateX(18px); }
                .kp-modal-footer {
                    padding: 16px 24px; border-top: 1px solid #f1f5f9;
                    display: flex; justify-content: flex-end; gap: 10px;
                }
                .kp-btn-cancel {
                    padding: 9px 18px; border-radius: 9px; border: 1.5px solid #e2e8f0;
                    background: #fff; color: #64748b;
                    font-size: 13px; font-weight: 600; font-family: inherit;
                    cursor: pointer; transition: background 0.15s;
                }
                .kp-btn-cancel:hover { background: #f1f5f9; }
                .kp-btn-save {
                    padding: 10px 20px; border-radius: 8px; border: none;
                    background: #cd6906; color: #fff;
                    font-size: 13px; font-weight: 600; font-family: inherit; cursor: pointer;
                    display: inline-flex; align-items: center; gap: 6px;
                    box-shadow: 0 4px 12px rgba(205,105,6,0.3);
                    transition: background 0.15s, transform 0.1s;
                }
                .kp-btn-save:hover:not(:disabled) { background: #b85d05; transform: translateY(-1px); }
                .kp-btn-save:disabled { opacity: 0.65; cursor: not-allowed; }
                .kp-spinner {
                    width: 14px; height: 14px;
                    border: 2px solid rgba(255,255,255,0.4);
                    border-top-color: #fff; border-radius: 50%;
                    animation: kpSpin 0.7s linear infinite;
                }
                @keyframes kpSpin { to { transform: rotate(360deg); } }

                /* ── Preview Modal ── */
                .kpv-overlay {
                    position: fixed; inset: 0;
                    background: rgba(8,15,30,0.72); backdrop-filter: blur(6px);
                    display: flex; align-items: center; justify-content: center;
                    z-index: 1100; padding: 20px;
                }
                .kpv-modal {
                    background: #fff; border-radius: 20px;
                    width: 100%; max-width: 980px; max-height: 92vh;
                    display: flex; flex-direction: column; overflow: hidden;
                    box-shadow: 0 32px 80px rgba(0,0,0,0.35);
                    animation: kpModalIn 0.2s ease;
                }
                .kpv-header {
                    padding: 20px 28px 16px; border-bottom: 1px solid #f1f5f9;
                    display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; flex-shrink: 0;
                }
                .kpv-header-info { display: flex; flex-direction: column; gap: 4px; }
                .kpv-title { font-size: 19px; font-weight: 800; color: #0f172a; margin: 0; letter-spacing: -0.3px; }
                .kpv-meta { display: flex; gap: 10px; align-items: center; flex-wrap: wrap; }
                .kpv-badge {
                    display: inline-flex; align-items: center; gap: 5px;
                    padding: 3px 10px 3px 7px; border-radius: 999px;
                    font-size: 11.5px; font-weight: 700;
                }
                .kpv-close {
                    flex-shrink: 0; width: 34px; height: 34px; border-radius: 10px; border: none;
                    background: #f1f5f9; color: #64748b; cursor: pointer;
                    display: flex; align-items: center; justify-content: center;
                    font-size: 17px; transition: background 0.15s;
                }
                .kpv-close:hover { background: #e2e8f0; color: #0f172a; }
                .kpv-nav-btn {
                    flex-shrink: 0; width: 34px; height: 34px; border-radius: 10px; border: none;
                    background: #f1f5f9; color: #475569; cursor: pointer;
                    display: flex; align-items: center; justify-content: center;
                    font-size: 14px; transition: background 0.15s; 
                }
                .kpv-nav-btn:hover:not(:disabled) { background: #e2e8f0; color: #0f172a; }
                .kpv-nav-btn:disabled { opacity: 0.3; cursor: not-allowed; }
                .kpv-nav-group { display: flex; align-items: center; gap: 4px; }
                .kpv-nav-count { font-size: 11.5px; font-weight: 600; color: #94a3b8; padding: 0 2px; }
                .kpv-body {
                    flex: 1; min-height: 0; overflow-y: auto;
                    padding: 24px 28px 28px; display: flex; flex-direction: column; gap: 28px;
                }
                /* Carousel */
                .kpv-carousel-wrap { position: relative; }
                .kpv-carousel {
                    width: 100%; height: 340px; border-radius: 14px; overflow: hidden;
                    background: #0f172a; position: relative; display: flex; align-items: center; justify-content: center;
                }
                .kpv-carousel img {
                    width: 100%; height: 100%; object-fit: contain; cursor: zoom-in;
                }
                .kpv-carousel-empty {
                    display: flex; flex-direction: column; align-items: center; justify-content: center;
                    color: #475569; gap: 8px; height: 100%;
                }
                .kpv-carousel-empty i { font-size: 42px; opacity: 0.3; }
                .kpv-carousel-empty span { font-size: 13px; opacity: 0.5; }
                .kpv-carr-btn {
                    position: absolute; top: 50%; transform: translateY(-50%);
                    width: 36px; height: 36px; border-radius: 50%; border: none;
                    background: rgba(255,255,255,0.9); color: #0f172a;
                    cursor: pointer; display: flex; align-items: center; justify-content: center;
                    font-size: 14px; box-shadow: 0 2px 10px rgba(0,0,0,0.2);
                    transition: background 0.15s; z-index: 2;
                }
                .kpv-carr-btn:hover { background: #fff; }
                .kpv-carr-prev { left: 10px; }
                .kpv-carr-next { right: 10px; }
                .kpv-carousel-dots {
                    display: flex; justify-content: center; gap: 6px; margin-top: 10px;
                }
                .kpv-dot {
                    width: 7px; height: 7px; border-radius: 50%;
                    background: #cbd5e1; cursor: pointer; transition: background 0.15s, transform 0.15s;
                }
                .kpv-dot.active { background: #cd6906; transform: scale(1.35); }
                .kpv-carousel-caption {
                    position: absolute; bottom: 0; left: 0; right: 0;
                    background: linear-gradient(transparent, rgba(0,0,0,0.55));
                    padding: 24px 16px 10px;
                    font-size: 12px; color: #fff; font-weight: 500; text-align: center;
                }
                /* Section headings */
                .kpv-section-title {
                    font-size: 13px; font-weight: 700; color: #64748b;
                    letter-spacing: 0.06em; text-transform: uppercase;
                    margin-bottom: 12px; display: flex; align-items: center; gap: 8px;
                }
                .kpv-section-title::before {
                    content: ''; display: inline-block; width: 3px; height: 14px;
                    background: #cd6906; border-radius: 2px;
                }
                /* Özellikler grid */
                .kpv-ozellik-grid {
                    display: grid; grid-template-columns: repeat(auto-fill, minmax(180px, 1fr)); gap: 10px;
                }
                .kpv-ozellik-card {
                    background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 10px;
                    padding: 10px 14px; display: flex; flex-direction: column; gap: 2px;
                }
                .kpv-ozellik-label { font-size: 10.5px; font-weight: 700; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.05em; }
                .kpv-ozellik-value { font-size: 14px; font-weight: 600; color: #0f172a; }
                /* Dosyalar */
                .kpv-file-list { display: flex; flex-direction: column; gap: 8px; }
                .kpv-file-row {
                    display: flex; align-items: center; gap: 12px;
                    padding: 10px 14px; border: 1px solid #e2e8f0; border-radius: 10px;
                    background: #f8fafc; text-decoration: none; color: #1e293b;
                    transition: background 0.13s, border-color 0.13s;
                }
                .kpv-file-row:hover { background: #f0f9ff; border-color: #bae6fd; }
                .kpv-file-icon { font-size: 20px; color: #64748b; flex-shrink: 0; }
                .kpv-file-name { font-size: 13px; font-weight: 600; flex: 1; }
                .kpv-file-type { font-size: 11px; color: #94a3b8; font-weight: 600; }
                /* Video grid */
                .kpv-video-grid { display: flex; flex-direction: column; gap: 12px; }
                .kpv-video-card {
                    border: 1px solid #e2e8f0; border-radius: 12px; overflow: hidden; background: #f8fafc;
                }
                .kpv-video-label {
                    padding: 8px 14px; font-size: 12px; font-weight: 600; color: #475569;
                    border-top: 1px solid #e2e8f0; background: #fff;
                }
                /* Info strip */
                .kpv-info-strip {
                    display: flex; gap: 20px; flex-wrap: wrap;
                    padding: 14px 16px; background: #f8fafc; border-radius: 12px;
                    border: 1px solid #e2e8f0;
                }
                .kpv-info-item { display: flex; flex-direction: column; gap: 2px; }
                .kpv-info-lbl { font-size: 10.5px; font-weight: 700; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.06em; }
                .kpv-info-val { font-size: 14px; font-weight: 600; color: #0f172a; }
                /* Lightbox */
                .kpv-lightbox-overlay {
                    position: fixed; inset: 0; background: rgba(0,0,0,0.92);
                    z-index: 1200; display: flex; align-items: center; justify-content: center;
                    cursor: zoom-out;
                }
                .kpv-lightbox-img { max-width: 90vw; max-height: 90vh; border-radius: 6px; object-fit: contain; }
                .kpv-lightbox-close {
                    position: absolute; top: 18px; right: 22px;
                    background: rgba(255,255,255,0.12); border: none; color: #fff;
                    border-radius: 50%; width: 38px; height: 38px; font-size: 18px;
                    cursor: pointer; display: flex; align-items: center; justify-content: center;
                }
                /* Loading */
                .kpv-loading {
                    display: flex; align-items: center; justify-content: center; height: 200px;
                    color: #94a3b8; font-size: 14px; gap: 10px;
                }
                .kpv-spinner {
                    width: 20px; height: 20px; border: 2.5px solid #e2e8f0;
                    border-top-color: #cd6906; border-radius: 50%;
                    animation: kpSpin 0.7s linear infinite;
                }
            `}</style>

            <div className="kp-page">
                {/* Header */}
                <div className="kp-header">
                    <div className="kp-header-left">
                        <h1 className="kp-title">Kiralık Portföyü</h1>
                        <p className="kp-subtitle">Kiralık ilanlarınızı ve mevcut durumlarını yönetin.</p>
                    </div>
                    <button className="kp-add-btn" onClick={() => setShowModal(true)}>
                        <i className="fas fa-plus" />
                        Yeni Gayrimenkul
                    </button>
                </div>

                {/* Stats */}
                <div className="kp-stats">
                    {[0, 1, 2].map(d => {
                        const count = gayrimenkuller.filter(g => g.durum === d).length;
                        if (!count) return null;
                        const dc = DURUM_COLORS[d];
                        return (
                            <div key={d} className="kp-stat">
                                <span style={{ width: 10, height: 10, borderRadius: 3, background: dc.dot }} />
                                <span className="kp-stat-num">{count}</span>
                                <span className="kp-stat-label">{DURUM_LABELS[d]}</span>
                            </div>
                        );
                    })}
                    <div className="kp-stat" style={{ marginLeft: 'auto' }}>
                        <span className="kp-stat-num">{gayrimenkuller.length}</span>
                        <span className="kp-stat-label">Toplam</span>
                    </div>
                </div>

                {/* Filters */}
                <div className="kp-filters">
                    <input
                        className="kp-search"
                        type="text"
                        placeholder="Başlık, takip no veya adres ara..."
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                    />
                    <div className="kp-filter-sep" />
                    <div className="kp-checkbox-group">
                        <span className="kp-checkbox-label">Kategori:</span>
                        {[{ v: 0, l: 'Daire' }, { v: 1, l: 'Ofis' }, { v: 2, l: 'Dükkân' }].map(opt => (
                            <label key={opt.v} className={`kp-checkbox-item ${filterKategori.includes(opt.v) ? 'active' : ''}`}>
                                <input type="checkbox" checked={filterKategori.includes(opt.v)}
                                    onChange={() => setFilterKategori(prev => prev.includes(opt.v) ? prev.filter(x => x !== opt.v) : [...prev, opt.v])} />
                                {opt.l}
                            </label>
                        ))}
                    </div>
                    <div className="kp-filter-sep" />
                    <div className="kp-checkbox-group">
                        <span className="kp-checkbox-label">Durum:</span>
                        {[{ v: 0, l: 'Boş' }, { v: 1, l: 'Kirada' }, { v: 2, l: 'Bakımda' }].map(opt => (
                            <label key={opt.v} className={`kp-checkbox-item ${filterDurum.includes(opt.v) ? 'active' : ''}`}>
                                <input type="checkbox" checked={filterDurum.includes(opt.v)}
                                    onChange={() => setFilterDurum(prev => prev.includes(opt.v) ? prev.filter(x => x !== opt.v) : [...prev, opt.v])} />
                                {opt.l}
                            </label>
                        ))}
                    </div>
                </div>

                {/* Table Card */}
                <div className="kp-card">
                    <table className="kp-table">
                        <thead>
                            <tr>
                                <th style={{ width: 72 }}></th>
                                <th style={{ width: 110 }}>Takip No</th>
                                <th>Gayrimenkul / İlan</th>
                                <th style={{ width: 90 }}>Kategori</th>
                                <th className="kp-th-right" style={{ width: 72 }}>m²</th>
                                <th style={{ width: 110 }}>Durum</th>
                                <th style={{ width: 120 }}>Proje</th>
                                <th className="kp-th-actions" style={{ width: 142 }}>İşlemler</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.length === 0 ? (
                                <tr>
                                    <td colSpan={8}>
                                        <div className="kp-empty">
                                            <div className="kp-empty-icon">
                                                <Home size={36} strokeWidth={1.2} color="#cbd5e1" />
                                            </div>
                                            <div className="kp-empty-text">
                                                {searchTerm || filterDurum.length > 0 || filterKategori.length > 0 ? 'Eşleşen kayıt bulunamadı' : 'Henüz gayrimenkul eklenmedi'}
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                filtered.map(g => {
                                    const dc = DURUM_COLORS[g.durum] || DURUM_COLORS[0];
                                    const hasThumb = thumbnails[g.takipNo] != null && !thumbErrors.has(g.takipNo);
                                    return (
                                        <tr
                                            key={g.gayrimenkulID}
                                            className={hoveredRow === g.gayrimenkulID ? 'kp-row-hovered' : ''}
                                            onMouseEnter={() => setHoveredRow(g.gayrimenkulID)}
                                            onMouseLeave={() => setHoveredRow(null)}
                                        >
                                            <td style={{ padding: '8px 8px 8px 18px' }}>
                                                <div className="kp-thumb">
                                                    {hasThumb ? (
                                                        <img
                                                            src={`${API_BASE_URL}/api/SunumBelgeleri/download/${thumbnails[g.takipNo]}`}
                                                            alt=""
                                                            onError={() => setThumbErrors(prev => new Set(prev).add(g.takipNo))}
                                                        />
                                                    ) : (
                                                        <span className="kp-thumb-empty">
                                                            <Home size={20} strokeWidth={1.5} color="#cbd5e1" />
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td><span className="kp-cell-takip">{g.takipNo}</span></td>
                                            <td>
                                                <div className="kp-cell-baslik">{g.baslik}</div>
                                                <div className="kp-cell-adres">{g.adres}</div>
                                            </td>
                                            <td><span className="kp-kategori-pill">{KATEGORI_LABELS[g.kategori] || '—'}</span></td>
                                            <td className="kp-cell-meta" style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{g.metrekare} m²</td>
                                            <td>
                                                <span className="kp-pill" style={{ background: dc.bg, color: dc.color }}>
                                                    <span className="kp-pill-dot" style={{ background: dc.dot }} />
                                                    {DURUM_LABELS[g.durum] || '—'}
                                                </span>
                                            </td>
                                            <td className="kp-cell-proje">{g.proje?.projeAdi || '—'}</td>
                                            <td>
                                                <div className="kp-actions">
                                                    <button className="kp-ghost-btn kp-ghost-view" title="Görüntüle" onClick={() => handleOpenPreview(g)}>
                                                        <i className="fas fa-eye" />
                                                    </button>
                                                    <button className="kp-ghost-btn kp-ghost-edit" title="Düzenle" onClick={() => handleEdit(g)}>
                                                        <i className="fas fa-pen" />
                                                    </button>
                                                    {g.durum === 0 && (
                                                        <button
                                                            className="kp-ghost-btn"
                                                            title="Sözleşme Yap"
                                                            style={{ color: '#16a34a' }}
                                                            onClick={() => navigate('/kiralik/sozlesmeler', { state: { gayrimenkulID: g.gayrimenkulID } })}
                                                        >
                                                            <i className="fas fa-file-contract" />
                                                        </button>
                                                    )}
                                                    <button className="kp-ghost-btn kp-ghost-delete" title="Pasife Al" onClick={() => handleDelete(g)}>
                                                        <i className="fas fa-trash-alt" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>

                    {filtered.length > 0 && (
                        <div className="kp-footer">
                            <span className="kp-count">Toplam <span>{filtered.length}</span> Gayrimenkul</span>
                            <span className="kp-count">
                                Boş: <span>{filtered.filter(g => g.durum === 0).length}</span>
                                &nbsp;&nbsp;Kirada: <span>{filtered.filter(g => g.durum === 1).length}</span>
                                &nbsp;&nbsp;Bakımda: <span>{filtered.filter(g => g.durum === 2).length}</span>
                            </span>
                        </div>
                    )}
                </div>
            </div>

            {/* Modal */}
            {showModal && (() => {
                const iSt: React.CSSProperties = { width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, outline: 'none', background: '#fff', color: '#111827', boxSizing: 'border-box' };
                const lSt: React.CSSProperties = { display: 'block', fontSize: 12, fontWeight: 500, color: '#374151', marginBottom: 4 };
                const iFocus = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => { e.target.style.borderColor = '#111827'; (e.target as HTMLElement).style.boxShadow = '0 0 0 3px rgba(17,24,39,0.08)'; };
                const iBlur  = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => { e.target.style.borderColor = '#d1d5db'; (e.target as HTMLElement).style.boxShadow = 'none'; };
                return (
                    <div
                        style={{ position: 'fixed', inset: 0, zIndex: 1050, display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                        onClick={handleCloseModal}
                    >
                        <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(2px)' }} />
                        <div style={{ position: 'relative', width: '100%', maxWidth: 672, maxHeight: '90vh', background: '#fff', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.2)', borderRadius: 16, overflow: 'hidden' }} onClick={e => e.stopPropagation()}>

                            {/* Header */}
                            <div style={{ flexShrink: 0, padding: 20, borderBottom: '1px solid #e5e7eb' }}>
                                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
                                        <div>
                                            <h2 style={{ fontSize: 18, fontWeight: 600, color: '#111827', margin: 0 }}>{selected ? 'Gayrimenkul Düzenle' : 'Yeni Gayrimenkul Ekle'}</h2>
                                            <p style={{ fontSize: 13, color: '#6b7280', margin: '4px 0 0' }}>Kiralık portföyünüze yeni bir gayrimenkul tanımlayın.</p>
                                        </div>
                                        {selected && (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginLeft: 12, border: '1px solid #e5e7eb', borderRadius: 8, padding: '4px 8px' }}>
                                                <button type="button" onClick={goEditPrev} disabled={currentEditIndex <= 0} style={{ background: 'none', border: 'none', cursor: currentEditIndex <= 0 ? 'not-allowed' : 'pointer', color: currentEditIndex <= 0 ? '#d1d5db' : '#374151', padding: '2px 4px', fontSize: 13 }}>&#8249;</button>
                                                <span style={{ fontSize: 12, color: '#6b7280', padding: '0 4px' }}>{currentEditIndex + 1} / {filtered.length}</span>
                                                <button type="button" onClick={goEditNext} disabled={currentEditIndex >= filtered.length - 1} style={{ background: 'none', border: 'none', cursor: currentEditIndex >= filtered.length - 1 ? 'not-allowed' : 'pointer', color: currentEditIndex >= filtered.length - 1 ? '#d1d5db' : '#374151', padding: '2px 4px', fontSize: 13 }}>&#8250;</button>
                                            </div>
                                        )}
                                    </div>
                                    <button onClick={handleCloseModal} style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 4, color: '#9ca3af', borderRadius: 6, display: 'flex', alignItems: 'center', transition: 'color 0.15s' }} onMouseEnter={e => (e.currentTarget.style.color = '#4b5563')} onMouseLeave={e => (e.currentTarget.style.color = '#9ca3af')}><X size={20} /></button>
                                </div>
                            </div>

                            {/* Body */}
                            <form onSubmit={handleSubmit} style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
                                <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 16, flex: 1 }}>

                                    {/* Başlık */}
                                    <div>
                                        <label style={lSt}>Başlık <span style={{ color: '#f43f5e' }}>*</span></label>
                                        <input type="text" value={formData.baslik} onChange={e => setFormData({ ...formData, baslik: e.target.value })} placeholder="Gayrimenkul adı / başlığı" required maxLength={200} autoFocus style={iSt} onFocus={iFocus} onBlur={iBlur} />
                                    </div>

                                    {/* Adres */}
                                    <div>
                                        <label style={lSt}>Adres <span style={{ color: '#f43f5e' }}>*</span></label>
                                        <textarea value={formData.adres} onChange={e => setFormData({ ...formData, adres: e.target.value })} placeholder="Açık adres" required maxLength={500} rows={2} style={{ ...iSt, resize: 'vertical', minHeight: 64, fontFamily: 'inherit' }} onFocus={iFocus} onBlur={iBlur} />
                                    </div>

                                    {/* Kategori + Durum */}
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                                        <div>
                                            <label style={lSt}>Kategori</label>
                                            <select value={formData.kategori} onChange={e => setFormData({ ...formData, kategori: parseInt(e.target.value) })} style={iSt} onFocus={iFocus} onBlur={iBlur}>
                                                <option value={0}>Daire</option>
                                                <option value={1}>Ofis</option>
                                                <option value={2}>Dükkân</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label style={lSt}>Durum</label>
                                            <select value={formData.durum} onChange={e => setFormData({ ...formData, durum: parseInt(e.target.value) })} disabled={selected?.durum === 1} style={iSt} onFocus={iFocus} onBlur={iBlur}>
                                                <option value={0}>Boş</option>
                                                <option value={2}>Bakımda</option>
                                                {selected?.durum === 1 && <option value={1}>Kirada</option>}
                                            </select>
                                            {selected?.durum === 1 && <span style={{ fontSize: 11, color: '#94a3b8', marginTop: 3, display: 'block' }}>Kirada durumu sözleşmeye bağlıdır</span>}
                                        </div>
                                    </div>

                                    {/* Metrekare + Kat */}
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                                        <div>
                                            <label style={lSt}>Metrekare (m²) <span style={{ color: '#f43f5e' }}>*</span></label>
                                            <input type="number" value={formData.metrekare || ''} onChange={e => setFormData({ ...formData, metrekare: parseInt(e.target.value) || 0 })} placeholder="120" required min={1} style={iSt} onFocus={iFocus} onBlur={iBlur} />
                                        </div>
                                        <div>
                                            <label style={lSt}>Kat</label>
                                            <input type="text" value={formData.kat} onChange={e => setFormData({ ...formData, kat: e.target.value })} placeholder="3. Kat" maxLength={50} style={iSt} onFocus={iFocus} onBlur={iBlur} />
                                        </div>
                                    </div>

                                    {/* Kira Bedeli + Proje */}
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                                        <div>
                                            <label style={lSt}>Kira Bedeli (₺)</label>
                                            <input type="text" value={formData.kiraBedeli ? formData.kiraBedeli.toLocaleString('tr-TR') : ''} onChange={e => { const raw = e.target.value.replace(/\./g, '').replace(',', '.'); const num = parseFloat(raw); setFormData({ ...formData, kiraBedeli: isNaN(num) ? 0 : num }); }} placeholder="0" style={iSt} onFocus={iFocus} onBlur={iBlur} />
                                        </div>
                                        <div>
                                            <label style={lSt}>Proje (Opsiyonel)</label>
                                            <select value={formData.projeID} onChange={e => setFormData({ ...formData, projeID: e.target.value })} style={iSt} onFocus={iFocus} onBlur={iBlur}>
                                                <option value="">Proje Seçin…</option>
                                                {projeler.map(p => <option key={p.projeID} value={p.projeID}>{p.projeAdi}</option>)}
                                            </select>
                                        </div>
                                    </div>

                                    {/* Aktif Toggle */}
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', border: '1px solid #e5e7eb', borderRadius: 8 }}>
                                        <span style={{ fontSize: 13, fontWeight: 500, color: '#374151' }}>Aktif</span>
                                        <div onClick={() => setFormData({ ...formData, aktifMi: !formData.aktifMi })} style={{ width: 44, height: 24, borderRadius: 12, background: formData.aktifMi ? '#cd6906' : '#d1d5db', cursor: 'pointer', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}>
                                            <div style={{ position: 'absolute', top: 3, left: formData.aktifMi ? 23 : 3, width: 18, height: 18, borderRadius: '50%', background: '#fff', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
                                        </div>
                                    </div>

                                    {/* Daire Özellikleri - sadece düzenleme modunda */}
                                    {selected && (
                                        <div style={{ marginTop: 4, borderTop: '1px solid #e2e8f0', paddingTop: 14 }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                                                <label style={{ fontWeight: 600, fontSize: 14, color: '#334155', margin: 0 }}>Daire Özellikleri</label>
                                                <button type="button" style={{ fontSize: 12, padding: '4px 10px', background: 'none', border: '1.5px solid #e2e8f0', borderRadius: 7, cursor: 'pointer', color: '#475569', fontFamily: 'inherit' }}
                                                    onClick={() => setDaireOzellikler([...daireOzellikler, { daireTakipNo: selected.takipNo, ozellik: '', deger: '', aktif: true }])}>
                                                    + Özellik Ekle
                                                </button>
                                            </div>
                                            {daireOzellikler.map((oz, idx) => (
                                                <div key={idx} style={{ display: 'flex', gap: 8, marginBottom: 6, alignItems: 'center' }}>
                                                    <input style={{ flex: 1, padding: '8px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', outline: 'none' }}
                                                        placeholder="Özellik adı" list="kp-ozellik-list"
                                                        value={oz.ozellik}
                                                        onChange={e => { const n = [...daireOzellikler]; n[idx] = { ...n[idx], ozellik: e.target.value }; setDaireOzellikler(n); }}
                                                        onBlur={() => fetchDegerOptions(oz.ozellik)} />
                                                    <input style={{ flex: 1, padding: '8px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', outline: 'none' }}
                                                        placeholder="Değer" list={`kp-deger-list-${idx}`}
                                                        value={oz.deger}
                                                        onChange={e => { const n = [...daireOzellikler]; n[idx] = { ...n[idx], deger: e.target.value }; setDaireOzellikler(n); }} />
                                                    <button type="button" style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 16, fontWeight: 700, padding: '0 6px' }}
                                                        onClick={async () => {
                                                            if (oz.daireOzellikId) {
                                                                try { await axios.delete(`/api/DaireOzellikler/${oz.daireOzellikId}`); } catch {}
                                                            }
                                                            setDaireOzellikler(daireOzellikler.filter((_, i) => i !== idx));
                                                        }}>✕</button>
                                                    <datalist id={`kp-deger-list-${idx}`}>
                                                        {(degerOptionsMap[oz.ozellik] || []).map(d => <option key={d} value={d} />)}
                                                    </datalist>
                                                </div>
                                            ))}
                                            <datalist id="kp-ozellik-list">
                                                {(ozellikOptions as string[]).map((o: string) => <option key={o} value={o} />)}
                                            </datalist>
                                        </div>
                                    )}

                                    {/* Sunum Belgeleri - sadece düzenleme modunda */}
                                    {selected && (
                                        <div style={{ marginTop: 4, borderTop: '1px solid #e2e8f0', paddingTop: 14 }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                                                <label style={{ fontWeight: 600, fontSize: 14, color: '#334155', margin: 0 }}>Sunum Belgeleri</label>
                                                {!showSunumEkleForm && (
                                                    <button type="button" style={{ fontSize: 12, padding: '4px 10px', background: 'none', border: '1.5px solid #e2e8f0', borderRadius: 7, cursor: 'pointer', color: '#475569', fontFamily: 'inherit' }}
                                                        onClick={() => setShowSunumEkleForm(true)}>+ Belge Ekle</button>
                                                )}
                                            </div>
                                            {showSunumEkleForm && (
                                                <div style={{ background: '#f8fafc', borderRadius: 8, padding: 12, border: '1px solid #e2e8f0', marginBottom: 12 }}>
                                                    <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                                                        <div style={{ flex: 1, minWidth: 140 }}>
                                                            <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>Açıklama</label>
                                                            <input style={{ width: '100%', padding: '8px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' }}
                                                                placeholder="Açıklama" value={sunumForm.aciklama}
                                                                onChange={e => setSunumForm(p => ({ ...p, aciklama: e.target.value }))} />
                                                        </div>
                                                        <div style={{ minWidth: 110 }}>
                                                            <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>Tür</label>
                                                            <select style={{ width: '100%', padding: '8px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', outline: 'none', height: 38 }}
                                                                value={sunumForm.tur} onChange={e => setSunumForm(p => ({ ...p, tur: e.target.value }))}>
                                                                <option value="Görsel">Görsel</option>
                                                                <option value="Belge">Belge</option>
                                                                <option value="Video">Video</option>
                                                            </select>
                                                        </div>
                                                    </div>
                                                    {sunumForm.tur === 'Video' ? (
                                                        <div style={{ marginBottom: 8 }}>
                                                            <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>Video Embed Kodu</label>
                                                            <textarea style={{ width: '100%', padding: '8px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', outline: 'none', resize: 'vertical', minHeight: 50, boxSizing: 'border-box' }}
                                                                placeholder='<iframe src="..." allowfullscreen></iframe>' value={sunumForm.videoLink}
                                                                onChange={e => setSunumForm(p => ({ ...p, videoLink: e.target.value }))} rows={3} />
                                                        </div>
                                                    ) : (
                                                        <div style={{ marginBottom: 8 }}>
                                                            <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>Dosya Seç</label>
                                                            <input type="file" style={{ width: '100%', padding: 6, border: '1.5px solid #e2e8f0', borderRadius: 8, fontFamily: 'inherit', boxSizing: 'border-box' }}
                                                                accept={sunumForm.tur === 'Görsel' ? 'image/*' : '.pdf,.doc,.docx,.xls,.xlsx'}
                                                                onChange={e => setSunumFile(e.target.files?.[0] || null)} />
                                                        </div>
                                                    )}
                                                    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                                                        <button type="button" style={{ fontSize: 12, padding: '5px 12px', background: 'none', border: '1.5px solid #e2e8f0', borderRadius: 7, cursor: 'pointer', fontFamily: 'inherit' }}
                                                            onClick={() => { setShowSunumEkleForm(false); setSunumFile(null); }}>Vazgeç</button>
                                                        <button type="button" style={{ fontSize: 12, padding: '5px 12px', background: '#cd6906', color: '#fff', border: 'none', borderRadius: 7, cursor: 'pointer', fontFamily: 'inherit' }}
                                                            onClick={handleSunumYukle} disabled={sunumBelgeSaving}>
                                                            {sunumBelgeSaving ? 'Yükleniyor...' : 'Yükle'}
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                            {sunumBelgeler.length === 0 && !showSunumEkleForm && (
                                                <div style={{ textAlign: 'center', color: '#94a3b8', padding: '8px 0', fontSize: 12 }}>Henüz sunum belgesi eklenmemiş.</div>
                                            )}
                                            {sunumBelgeler.length > 0 && (
                                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: 8 }}>
                                                    {sunumBelgeler.map(b => (
                                                        <div key={b.sunumBelgeId} style={{ border: '1px solid #e2e8f0', borderRadius: 6, overflow: 'hidden', background: '#fff', position: 'relative' }}>
                                                            <button type="button" onClick={() => handleSunumSil(b.sunumBelgeId)}
                                                                style={{ position: 'absolute', top: 2, right: 2, background: 'rgba(255,255,255,0.9)', border: 'none', borderRadius: '50%', width: 18, height: 18, cursor: 'pointer', color: '#dc2626', fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2 }}
                                                                title="Sil">✕</button>
                                                            <div style={{ height: 70, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc', cursor: 'pointer' }}
                                                                onClick={() => {
                                                                    if (b.sunumBelgeTuru !== 'Video') window.open(`${API_BASE_URL}/api/SunumBelgeleri/download/${b.sunumBelgeId}`, '_blank');
                                                                }}>
                                                                {b.sunumBelgeTuru === 'Görsel' && (
                                                                    <img src={`${API_BASE_URL}/api/SunumBelgeleri/thumbnail/${b.sunumBelgeId}`} alt={b.aciklama || ''}
                                                                        style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'cover' }} />
                                                                )}
                                                                {b.sunumBelgeTuru === 'Belge' && (
                                                                    <div style={{ textAlign: 'center', color: '#64748b' }}><i className="fas fa-file-alt" style={{ fontSize: 22 }}></i><div style={{ fontSize: 9, marginTop: 2 }}>Belge</div></div>
                                                                )}
                                                                {b.sunumBelgeTuru === 'Video' && (
                                                                    <div style={{ textAlign: 'center', color: '#64748b' }}><i className="fas fa-play-circle" style={{ fontSize: 22 }}></i><div style={{ fontSize: 9, marginTop: 2 }}>Video</div></div>
                                                                )}
                                                            </div>
                                                            <div style={{ padding: '4px 6px', borderTop: '1px solid #e2e8f0' }}>
                                                                <div style={{ fontSize: 10, fontWeight: 600, color: '#334155', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                                    {b.aciklama || b.sunumBelgeTuru}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {/* Footer */}
                                <div style={{ flexShrink: 0, padding: '20px', borderTop: '1px solid #e5e7eb', background: '#fff', display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
                                    <button type="button" onClick={handleCloseModal} style={{ padding: '10px 20px', background: '#fff', border: '1px solid #d1d5db', color: '#374151', borderRadius: 8, fontSize: 14, fontWeight: 500, cursor: 'pointer', transition: 'background 0.15s' }} onMouseEnter={e => (e.currentTarget.style.background = '#f9fafb')} onMouseLeave={e => (e.currentTarget.style.background = '#fff')}>İptal</button>
                                    <button type="submit" disabled={saving} style={{ padding: '10px 24px', background: saving ? '#e5a44d' : '#cd6906', border: 'none', color: '#fff', borderRadius: 8, fontSize: 14, fontWeight: 500, cursor: saving ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 8, boxShadow: '0 1px 3px rgba(205,105,6,0.3)', transition: 'background 0.15s, box-shadow 0.15s' }} onMouseEnter={e => { if (!saving) { e.currentTarget.style.background = '#a85505'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(205,105,6,0.4)'; }}} onMouseLeave={e => { if (!saving) { e.currentTarget.style.background = '#cd6906'; e.currentTarget.style.boxShadow = '0 1px 3px rgba(205,105,6,0.3)'; }}}>
                                        {saving ? 'Kaydediliyor...' : <><Check size={16} />{selected ? 'Güncelle' : 'Kaydet'}</>}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                );
            })()}
            {/* ── Sunum / Önizleme Modal ── */}
            {showPreview && previewGayrimenkul && (() => {
                const g = previewGayrimenkul;
                const dc = DURUM_COLORS[g.durum] || DURUM_COLORS[0];
                const gorseller = previewBelgeler.filter(b => b.sunumBelgeTuru === 'Görsel');
                const videolar   = previewBelgeler.filter(b => b.sunumBelgeTuru === 'Video');
                const dosyalar   = previewBelgeler.filter(b => b.sunumBelgeTuru === 'Belge');
                const safeIdx = Math.min(carouselIndex, Math.max(gorseller.length - 1, 0));
                return (
                    <div className="kpv-overlay" onClick={handleClosePreview}>
                        <div className="kpv-modal" onClick={e => e.stopPropagation()}>
                            {/* Header */}
                            <div className="kpv-header">
                                <div className="kpv-header-info">
                                    <h2 className="kpv-title">{g.baslik}</h2>
                                    <div className="kpv-meta">
                                        <span className="kpv-badge" style={{ background: dc.bg, color: dc.color }}>
                                            <span style={{ width: 7, height: 7, borderRadius: '50%', background: dc.dot, display: 'inline-block' }} />
                                            {DURUM_LABELS[g.durum]}
                                        </span>
                                        <span style={{ fontSize: 12, color: '#94a3b8', fontWeight: 600 }}>{KATEGORI_LABELS[g.kategori]}</span>
                                        <span style={{ fontSize: 12, color: '#475569' }}>{g.adres}</span>
                                    </div>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <div className="kpv-nav-group">
                                        <button type="button" className="kpv-nav-btn" title="Önceki" onClick={goPreviewPrev} disabled={currentPreviewIndex <= 0}>
                                            <i className="fas fa-chevron-left" />
                                        </button>
                                        <span className="kpv-nav-count">{currentPreviewIndex + 1} / {filtered.length}</span>
                                        <button type="button" className="kpv-nav-btn" title="Sonraki" onClick={goPreviewNext} disabled={currentPreviewIndex >= filtered.length - 1}>
                                            <i className="fas fa-chevron-right" />
                                        </button>
                                    </div>
                                    <button className="kpv-close" onClick={handleClosePreview}>✕</button>
                                </div>
                            </div>

                            {/* Body */}
                            <div className="kpv-body">
                                {previewLoading ? (
                                    <div className="kpv-loading">
                                        <span className="kpv-spinner" />
                                        Yükleniyor...
                                    </div>
                                ) : (
                                    <>
                                        {/* Info Strip */}
                                        <div className="kpv-info-strip">
                                            <div className="kpv-info-item">
                                                <span className="kpv-info-lbl">Takip No</span>
                                                <span className="kpv-info-val" style={{ fontFamily: 'monospace', fontSize: 13 }}>{g.takipNo}</span>
                                            </div>
                                            <div className="kpv-info-item">
                                                <span className="kpv-info-lbl">Alan</span>
                                                <span className="kpv-info-val">{g.metrekare} m²</span>
                                            </div>
                                            {g.kat && (
                                                <div className="kpv-info-item">
                                                    <span className="kpv-info-lbl">Kat</span>
                                                    <span className="kpv-info-val">{g.kat}</span>
                                                </div>
                                            )}
                                            {g.proje && (
                                                <div className="kpv-info-item">
                                                    <span className="kpv-info-lbl">Proje</span>
                                                    <span className="kpv-info-val">{g.proje.projeAdi}</span>
                                                </div>
                                            )}
                                            {g.kiraBedeli > 0 && (
                                                <div className="kpv-info-item">
                                                    <span className="kpv-info-lbl">Kira Bedeli</span>
                                                    <span className="kpv-info-val" style={{ color: '#15803d' }}>
                                                        {g.kiraBedeli.toLocaleString('tr-TR')} ₺
                                                    </span>
                                                </div>
                                            )}
                                        </div>

                                        {/* Carousel */}
                                        {gorseller.length > 0 && (
                                            <div className="kpv-carousel-wrap">
                                                <div className="kpv-section-title">Görseller</div>
                                                <div className="kpv-carousel">
                                                    <img
                                                        src={`${API_BASE_URL}/api/SunumBelgeleri/download/${gorseller[safeIdx].sunumBelgeId}`}
                                                        alt={gorseller[safeIdx].aciklama || ''}
                                                        onClick={() => setLightboxBelge(gorseller[safeIdx])}
                                                    />
                                                    {gorseller[safeIdx].aciklama && (
                                                        <div className="kpv-carousel-caption">{gorseller[safeIdx].aciklama}</div>
                                                    )}
                                                    {gorseller.length > 1 && (
                                                        <>
                                                            <button className="kpv-carr-btn kpv-carr-prev"
                                                                onClick={() => setCarouselIndex((safeIdx - 1 + gorseller.length) % gorseller.length)}>
                                                                <i className="fas fa-chevron-left" />
                                                            </button>
                                                            <button className="kpv-carr-btn kpv-carr-next"
                                                                onClick={() => setCarouselIndex((safeIdx + 1) % gorseller.length)}>
                                                                <i className="fas fa-chevron-right" />
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                                {gorseller.length > 1 && (
                                                    <div className="kpv-carousel-dots">
                                                        {gorseller.map((_, i) => (
                                                            <span key={i} className={`kpv-dot${i === safeIdx ? ' active' : ''}`}
                                                                onClick={() => setCarouselIndex(i)} />
                                                        ))}
                                                    </div>
                                                )}
                                                {/* Thumbnail strip */}
                                                {gorseller.length > 1 && (
                                                    <div style={{ display: 'flex', gap: 8, marginTop: 10, overflowX: 'auto', paddingBottom: 4 }}>
                                                        {gorseller.map((b, i) => (
                                                            <img key={b.sunumBelgeId}
                                                                src={`${API_BASE_URL}/api/SunumBelgeleri/download/${b.sunumBelgeId}`}
                                                                alt={b.aciklama || ''}
                                                                onClick={() => setCarouselIndex(i)}
                                                                style={{
                                                                    width: 72, height: 52, objectFit: 'cover', borderRadius: 8, flexShrink: 0,
                                                                    cursor: 'pointer', border: i === safeIdx ? '2px solid #cd6906' : '2px solid transparent',
                                                                    opacity: i === safeIdx ? 1 : 0.6, transition: 'opacity 0.15s, border-color 0.15s',
                                                                }}
                                                            />
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* Daire Özellikleri */}
                                        {previewOzellikler.length > 0 && (
                                            <div>
                                                <div className="kpv-section-title">Özellikler</div>
                                                <div className="kpv-ozellik-grid">
                                                    {previewOzellikler.map((oz, i) => (
                                                        <div key={oz.daireOzellikId ?? i} className="kpv-ozellik-card">
                                                            <span className="kpv-ozellik-label">{oz.ozellik}</span>
                                                            <span className="kpv-ozellik-value">{oz.deger}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* Videolar */}
                                        {videolar.length > 0 && (
                                            <div>
                                                <div className="kpv-section-title">Videolar</div>
                                                <div className="kpv-video-grid">
                                                    {videolar.map(b => (
                                                        <div key={b.sunumBelgeId} className="kpv-video-card">
                                                            {b.sunumBelgeAdi && (
                                                                <div style={{ borderRadius: 'inherit', overflow: 'hidden' }}
                                                                    dangerouslySetInnerHTML={{ __html: b.sunumBelgeAdi }} />
                                                            )}
                                                            {b.aciklama && <div className="kpv-video-label">{b.aciklama}</div>}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* Dosyalar */}
                                        {dosyalar.length > 0 && (
                                            <div>
                                                <div className="kpv-section-title">Belgeler</div>
                                                <div className="kpv-file-list">
                                                    {dosyalar.map(b => (
                                                        <a key={b.sunumBelgeId}
                                                            href={`${API_BASE_URL}/api/SunumBelgeleri/download/${b.sunumBelgeId}`}
                                                            target="_blank" rel="noreferrer"
                                                            className="kpv-file-row">
                                                            <i className="fas fa-file-alt kpv-file-icon" />
                                                            <span className="kpv-file-name">{b.aciklama || b.sunumBelgeAdi || 'Belge'}</span>
                                                            <span className="kpv-file-type">PDF/Dosya</span>
                                                            <i className="fas fa-download" style={{ color: '#94a3b8', fontSize: 13 }} />
                                                        </a>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* Hiç içerik yoksa */}
                                        {previewOzellikler.length === 0 && previewBelgeler.length === 0 && (
                                            <div style={{ textAlign: 'center', color: '#94a3b8', padding: '40px 0', fontSize: 14 }}>
                                                <i className="fas fa-image" style={{ fontSize: 36, marginBottom: 12, display: 'block', opacity: 0.25 }} />
                                                Bu gayrimenkul için henüz özellik veya sunum belgesi eklenmemiş.
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>

                        {/* Lightbox */}
                        {lightboxBelge && (
                            <div className="kpv-lightbox-overlay" onClick={() => setLightboxBelge(null)}>
                                <button className="kpv-lightbox-close" onClick={() => setLightboxBelge(null)}>✕</button>
                                <img
                                    className="kpv-lightbox-img"
                                    src={`${API_BASE_URL}/api/SunumBelgeleri/download/${lightboxBelge.sunumBelgeId}`}
                                    alt={lightboxBelge.aciklama || ''}
                                    onClick={e => e.stopPropagation()}
                                />
                            </div>
                        )}
                    </div>
                );
            })()}
        </>
    );
};

export default KiralikPortfoy;
